import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { 
  Calendar as CalendarIcon, 
  Church, 
  RefreshCw, 
  Plus, 
  Edit2, 
  Trash2, 
  Clock,
  AlertCircle,
  ExternalLink,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';

const CHURCHES = [
  { id: 'karlskrona', name: 'Karlskrona - Vår Fru av Fatima kyrka', color: 'bg-blue-500' },
  { id: 'karlshamn', name: 'Karlshamn - S:t Bonifatius kyrka', color: 'bg-green-500' },
  { id: 'olofstrom', name: 'Olofström - S:t Antonius kyrka', color: 'bg-purple-500' },
];

const EVENT_TYPES = [
  { id: 'mass', label: 'Mässa' },
  { id: 'event', label: 'Aktivitet' },
  { id: 'confession', label: 'Bikt' },
  { id: 'adoration', label: 'Tillbedjan' },
  { id: 'rosary', label: 'Rosenkrans' },
  { id: 'other', label: 'Annat' },
];

const LiturgiManager = () => {
  const [loading, setLoading] = useState(true);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    church: 'karlskrona',
    start_datetime: '',
    calendar_type: 'mass',
    location: '',
    language: '',
  });

  // Fetch all calendar events from admin endpoint
  const fetchCalendarEvents = useCallback(async () => {
    try {
      const events = await api.getAdminCalendarEvents();
      setCalendarEvents(events);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      toast.error('Kunde inte hämta händelser');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCalendarEvents();
      setLoading(false);
    };
    loadData();
  }, [fetchCalendarEvents]);

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      church: 'karlskrona',
      start_datetime: '',
      calendar_type: 'mass',
      location: '',
      language: '',
    });
    setEditingEvent(null);
  };

  // Open create dialog
  const openCreateDialog = () => {
    resetForm();
    // Set default datetime to tomorrow at 10:00
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(10, 0, 0, 0);
    const tomorrowStr = tomorrow.toISOString().slice(0, 16);
    setFormData(prev => ({
      ...prev,
      start_datetime: tomorrowStr,
    }));
    setShowDialog(true);
  };

  // Open edit dialog
  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title || '',
      description: event.description || '',
      church: event.church || 'karlskrona',
      start_datetime: event.start_datetime?.slice(0, 16) || '',
      calendar_type: event.calendar_type || 'mass',
      location: event.location || '',
      language: event.language || '',
    });
    setShowDialog(true);
  };

  // Save event
  const handleSave = async () => {
    if (!formData.title.trim() || !formData.start_datetime) {
      toast.error('Titel och datum krävs');
      return;
    }

    try {
      if (editingEvent) {
        await api.updateAdminCalendarEvent(editingEvent.uid, formData);
        toast.success('Händelse uppdaterad!');
      } else {
        await api.createAdminCalendarEvent(formData);
        toast.success('Händelse skapad!');
      }
      setShowDialog(false);
      resetForm();
      await fetchCalendarEvents();
    } catch (error) {
      toast.error('Kunde inte spara händelse');
    }
  };

  // Delete event
  const handleDelete = async (eventUid) => {
    if (!window.confirm('Ta bort denna händelse?')) return;
    
    try {
      await api.deleteAdminCalendarEvent(eventUid);
      toast.success('Händelse borttagen!');
      await fetchCalendarEvents();
    } catch (error) {
      toast.error('Kunde inte ta bort händelse');
    }
  };

  // Group events by date
  const groupEventsByDate = (events) => {
    const grouped = {};
    events.forEach(event => {
      const date = new Date(event.start_datetime).toLocaleDateString('sv-SE');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(event);
    });
    // Sort dates
    const sortedDates = Object.keys(grouped).sort();
    return sortedDates.map(date => ({
      date,
      events: grouped[date].sort((a, b) => 
        new Date(a.start_datetime) - new Date(b.start_datetime)
      )
    }));
  };

  // Filter events by church and type
  const filterEvents = (events) => {
    let filtered = events;
    
    // Filter by church
    if (activeTab !== 'all') {
      filtered = filtered.filter(e => e.church === activeTab);
    }
    
    // Filter by event type
    if (filterType !== 'all') {
      filtered = filtered.filter(e => e.calendar_type === filterType);
    }
    
    return filtered;
  };

  // Get church stats
  const getChurchStats = (churchId) => {
    return calendarEvents.filter(e => e.church === churchId).length;
  };

  const filteredEvents = filterEvents(calendarEvents);
  const groupedEvents = groupEventsByDate(filteredEvents);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Liturgi</h2>
          <p className="text-gray-600">Hantera mässor och liturgiska evenemang importerade från iCal</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" />
          Lägg till händelse
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Händelser importeras via Dashboard → Kalender-integration (iCal)</p>
          <p>Här kan du visa, redigera och manuellt lägga till liturgiska händelser. För att synkronisera nya händelser från CalDAV, gå till Dashboard.</p>
        </div>
      </div>

      {/* Church Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {CHURCHES.map(church => (
          <Card key={church.id} className="overflow-hidden">
            <div className={`h-2 ${church.color}`} />
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Church className="w-5 h-5 text-gray-600" />
                  <h3 className="font-medium text-sm">{church.name.split(' - ')[0]}</h3>
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">{getChurchStats(church.id)}</p>
              <p className="text-xs text-gray-500">händelser</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Events List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-amber-600" />
            Alla liturgiska händelser ({filteredEvents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            {/* Church Filter */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">Alla kyrkor</TabsTrigger>
                {CHURCHES.map(church => (
                  <TabsTrigger key={church.id} value={church.id}>
                    {church.name.split(' - ')[0]}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">Alla typer</option>
              {EVENT_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Events grouped by date */}
          {groupedEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Inga händelser hittades</p>
              <p className="text-sm mt-1">Importera händelser via Dashboard eller lägg till manuellt</p>
            </div>
          ) : (
            <div className="space-y-6 max-h-[600px] overflow-y-auto">
              {groupedEvents.map(({ date, events }) => (
                <div key={date}>
                  <h3 className="font-semibold text-gray-700 mb-3 pb-2 border-b sticky top-0 bg-white">
                    {new Date(date).toLocaleDateString('sv-SE', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </h3>
                  <div className="space-y-3">
                    {events.map((event, idx) => {
                      const church = CHURCHES.find(c => c.id === event.church);
                      const eventType = EVENT_TYPES.find(t => t.id === event.calendar_type);
                      const startTime = new Date(event.start_datetime).toLocaleTimeString('sv-SE', {
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                      
                      return (
                        <div
                          key={event.uid || idx}
                          className={`p-4 rounded-lg border-l-4 bg-gray-50 ${
                            church?.color?.replace('bg-', 'border-') || 'border-gray-300'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <span className="font-medium text-gray-900">{startTime}</span>
                                <span className={`text-xs px-2 py-0.5 rounded ${church?.color} text-white`}>
                                  {church?.name.split(' - ')[0]}
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-700">
                                  {eventType?.label || event.calendar_type}
                                </span>
                                {event.source === 'manual' && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                                    Manuell
                                  </span>
                                )}
                              </div>
                              <h4 className="font-semibold text-gray-900">{event.title}</h4>
                              {event.description && (
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{event.description}</p>
                              )}
                              {event.location && (
                                <p className="text-xs text-gray-500 mt-1">📍 {event.location}</p>
                              )}
                              {event.language && (
                                <p className="text-xs text-gray-500">🌐 {event.language}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(event)}
                                title="Redigera"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete(event.uid)}
                                title="Ta bort"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Link */}
      <div className="flex justify-center">
        <a
          href="/liturgi"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
        >
          <ExternalLink className="w-4 h-4" />
          Förhandsgranska liturgisidan
        </a>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? 'Redigera händelse' : 'Ny händelse'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titel *
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="t.ex. Påskmässa"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kyrka
                </label>
                <select
                  value={formData.church}
                  onChange={(e) => setFormData({ ...formData, church: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  {CHURCHES.map(church => (
                    <option key={church.id} value={church.id}>{church.name.split(' - ')[0]}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typ
                </label>
                <select
                  value={formData.calendar_type}
                  onChange={(e) => setFormData({ ...formData, calendar_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  {EVENT_TYPES.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Datum och tid *
              </label>
              <Input
                type="datetime-local"
                value={formData.start_datetime}
                onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plats
              </label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="t.ex. Vår Fru av Fatima kyrka"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Språk
              </label>
              <Input
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                placeholder="t.ex. Svenska, Engelska"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Beskrivning
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ytterligare information..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Avbryt
            </Button>
            <Button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700">
              {editingEvent ? 'Uppdatera' : 'Skapa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiturgiManager;
